import { Linkedin, Twitter, Quote, Mail, Sparkles } from "lucide-react";
import { FOUNDER } from "@/constants/testIds";
import { MarketFriezeMini } from "@/components/landing/MarketFrieze";
import { useContent } from "@/context/ContentContext";

export const Founder = () => {
  const { content } = useContent();
  const f = content.founder;
  const photoSrc = f.photo
    ? f.photo.startsWith("http") || f.photo.startsWith("data:")
      ? f.photo
      : `${process.env.REACT_APP_BACKEND_URL || ""}${f.photo}`
    : "";

  return (
    <section
      id="founder"
      data-testid={FOUNDER.root}
      className="relative py-24 md:py-32 bg-[#FBFAF6] border-y border-[#EDE7D9]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-[#0E7A3B] via-[#0E7A3B]/70 to-[#F5A800] relative shadow-[0_30px_80px_rgba(22,36,27,0.2)] -rotate-2 hover:rotate-0 transition-transform duration-700">
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt={f.name}
                    data-testid="founder-photo"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                      <p className="font-display font-bold text-[9rem] leading-none text-white/95 tracking-tighter">
                        {(f.name || "W")
                          .split(" ")
                          .map((p) => p[0])
                          .slice(0, 2)
                          .join("")}
                      </p>
                      <p className="mt-2 font-utility text-xs uppercase tracking-[0.3em] text-white/80">
                        Portrait à fournir
                      </p>
                    </div>
                    <div className="absolute inset-0 pointer-events-none noise-overlay" />
                  </>
                )}
              </div>

              <div className="absolute -bottom-5 -right-5 rounded-2xl bg-white border border-[#EDE7D9] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.12)] max-w-[240px]">
                <p className="font-utility text-[10px] uppercase tracking-widest text-[#0E7A3B]">
                  Fondateur
                </p>
                <p className="mt-1 font-display text-lg text-[#16241B] tracking-tight">
                  {f.name}
                </p>
                <p className="mt-0.5 text-xs text-[#16241B]/60">
                  {f.role}
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">
              Le fondateur
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-6xl text-[#16241B] leading-[1.02] tracking-tighter">
              La personne
              <br />
              <span className="text-[#0E7A3B]">derrière Zafara.</span>
            </h2>
            <MarketFriezeMini className="mt-6 max-w-[200px]" />

            <div className="mt-8 text-[#16241B]/85 text-lg leading-relaxed max-w-2xl whitespace-pre-line">
              {f.bio}
            </div>

            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-white border border-[#EDE7D9] px-4 py-2 text-sm">
              <Sparkles className="h-4 w-4 text-[#F5A800]" />
              <span className="text-[#16241B]/70">
                Également fondateur de{" "}
                <span className="text-[#0E7A3B] font-semibold">
                  Ourbeauty Bénin
                </span>{" "}
                — cosmétiques au lait de chèvre et d'ânesse.
              </span>
            </div>

            <div className="mt-10 relative pl-8 max-w-2xl">
              <Quote className="absolute left-0 top-0 h-6 w-6 text-[#F5A800]" />
              <p className="font-display text-2xl md:text-3xl text-[#16241B] leading-snug italic tracking-tight">
                « {f.quote} »
              </p>
              <p className="mt-4 font-utility text-xs uppercase tracking-[0.25em] text-[#16241B]/60">
                {f.name} · {f.role}
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              {f.email && (
                <a
                  href={`mailto:${f.email}`}
                  className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-5 py-2.5 text-sm font-medium text-[#16241B] hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
                >
                  <Mail className="h-4 w-4" /> {f.email}
                </a>
              )}
              {f.linkedin && (
                <a
                  href={f.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-5 py-2.5 text-sm font-medium text-[#16241B] hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
                >
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>
              )}
              {f.twitter && (
                <a
                  href={f.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-5 py-2.5 text-sm font-medium text-[#16241B] hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
                >
                  <Twitter className="h-4 w-4" /> Twitter / X
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
